import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { mediaAPI } from '../../lib/api';

export default function AdminMedia() {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMedia = async () => {
    try {
      const response = await mediaAPI.getAll();
      setMediaItems(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const handleDelete = async (id) => {
    try {
      await mediaAPI.delete(id);
      toast.success('Media deleted successfully');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Media Coverage
          </h1>
          <p className="text-muted-foreground">
            Manage news articles and media mentions.
          </p>
        </div>
        <Link to="/admin/media/new">
          <Button data-testid="new-media-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Media
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-background p-4 border border-border animate-pulse">
              <div className="h-6 bg-muted w-1/3 mb-2"></div>
              <div className="h-4 bg-muted w-1/2"></div>
            </div>
          ))}
        </div>
      ) : mediaItems.length > 0 ? (
        <div className="bg-background border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium text-foreground">Title</th>
                  <th className="text-left p-4 font-medium text-foreground">Source</th>
                  <th className="text-left p-4 font-medium text-foreground">Date</th>
                  <th className="text-right p-4 font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mediaItems.map((item) => (
                  <tr key={item.id} className="border-t border-border">
                    <td className="p-4">
                      <span className="text-foreground font-medium">{item.title}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-accent">{item.source}</span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(item.date).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/media/${item.id}/edit`}>
                          <Button variant="ghost" size="sm" data-testid={`edit-media-${item.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        {item.url && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Media</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this media entry?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(item.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-background border border-border p-12 text-center">
          <h3 className="font-heading text-xl text-foreground mb-2">No Media Coverage Yet</h3>
          <p className="text-muted-foreground mb-6">Add your first media mention.</p>
          <Link to="/admin/media/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Media
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
